/* ============================================================
   StylistFlipCard — Premium 3D flip card for stylists
   Front: photo, bio, book CTA  |  Back: recommended products
   Features: gradient glow border, hover image swap,
   collapsible bio, 8 color presets, 3D perspective flip
   ============================================================ */

import { useState } from 'react'
import './stylist-flip-card.css'

/* ── Color Preset System ──────────────────────────────── */

interface ColorPreset {
  frameGradient: string
  glowFrom: string
  glowTo: string
  buttonBg: string
  buttonText: string
  accentText: string
  bgGradient: string
}

const PRESETS: Record<string, ColorPreset> = {
  default: {
    frameGradient: 'linear-gradient(135deg, #a78bfa, #f472b6)',
    glowFrom: 'hsl(270 80% 60% / 0.25)',
    glowTo: 'hsl(330 80% 60% / 0.15)',
    buttonBg: '#a78bfa',
    buttonText: '#fff',
    accentText: '#a78bfa',
    bgGradient: 'linear-gradient(160deg, #1a1625, #0f0a1a)',
  },
  teal: {
    frameGradient: 'linear-gradient(135deg, #2dd4bf, #06b6d4)',
    glowFrom: 'hsl(170 70% 50% / 0.25)',
    glowTo: 'hsl(190 80% 50% / 0.15)',
    buttonBg: '#2dd4bf',
    buttonText: '#0f172a',
    accentText: '#2dd4bf',
    bgGradient: 'linear-gradient(160deg, #0f2922, #0a1a1a)',
  },
  rose: {
    frameGradient: 'linear-gradient(135deg, #fb7185, #f43f5e)',
    glowFrom: 'hsl(350 80% 60% / 0.25)',
    glowTo: 'hsl(0 80% 60% / 0.15)',
    buttonBg: '#fb7185',
    buttonText: '#fff',
    accentText: '#fb7185',
    bgGradient: 'linear-gradient(160deg, #1a1015, #1a0a0f)',
  },
  amber: {
    frameGradient: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
    glowFrom: 'hsl(45 90% 50% / 0.25)',
    glowTo: 'hsl(35 90% 50% / 0.15)',
    buttonBg: '#fbbf24',
    buttonText: '#1a1a1a',
    accentText: '#fbbf24',
    bgGradient: 'linear-gradient(160deg, #1a1608, #1a120a)',
  },
  violet: {
    frameGradient: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    glowFrom: 'hsl(260 70% 60% / 0.3)',
    glowTo: 'hsl(280 70% 50% / 0.15)',
    buttonBg: '#8b5cf6',
    buttonText: '#fff',
    accentText: '#8b5cf6',
    bgGradient: 'linear-gradient(160deg, #1a1030, #0f0820)',
  },
  'neon-twilight': {
    frameGradient: 'linear-gradient(135deg, #22d3ee, #e879f9)',
    glowFrom: 'hsl(190 80% 55% / 0.3)',
    glowTo: 'hsl(290 80% 65% / 0.2)',
    buttonBg: '#22d3ee',
    buttonText: '#0f172a',
    accentText: '#e879f9',
    bgGradient: 'linear-gradient(160deg, #0a1520, #1a0a25)',
  },
  sunset: {
    frameGradient: 'linear-gradient(135deg, #f97316, #ef4444)',
    glowFrom: 'hsl(25 90% 55% / 0.3)',
    glowTo: 'hsl(0 80% 55% / 0.15)',
    buttonBg: '#f97316',
    buttonText: '#fff',
    accentText: '#f97316',
    bgGradient: 'linear-gradient(160deg, #1a1008, #1a0808)',
  },
  emerald: {
    frameGradient: 'linear-gradient(135deg, #34d399, #10b981)',
    glowFrom: 'hsl(160 60% 50% / 0.25)',
    glowTo: 'hsl(150 60% 45% / 0.15)',
    buttonBg: '#34d399',
    buttonText: '#0f172a',
    accentText: '#34d399',
    bgGradient: 'linear-gradient(160deg, #0a1a15, #081a12)',
  },
}

function getPreset(name?: string): ColorPreset {
  return PRESETS[name || 'default'] || PRESETS.default
}

/* ── Component Props ──────────────────────────────────── */

interface StylistFlipCardProps {
  data: Record<string, string>
  entityData?: Record<string, unknown>
}

/* ── Demo Products (placeholder) ──────────────────────── */

const DEMO_PRODUCTS = [
  { name: 'Olaplex No.3', brand: 'Olaplex', desc: 'Hair perfector treatment' },
  { name: 'Color Wow Dream Coat', brand: 'Color Wow', desc: 'Anti-frizz treatment' },
  { name: 'K18 Leave-In Mask', brand: 'K18', desc: 'Molecular repair mask' },
]

/* ── Main Component ───────────────────────────────────── */

export default function StylistFlipCard({ data, entityData }: StylistFlipCardProps) {
  const [isFlipped, setIsFlipped] = useState(false)
  const [bioExpanded, setBioExpanded] = useState(false)

  // Resolve data from entity or card data
  const name = (entityData?.name as string) || data.name || 'Alex Rivera'
  const title =
    (entityData?.title as string) ||
    (entityData?.specialty as string) ||
    data.title ||
    'Senior Colorist'
  const bio =
    (entityData?.bio as string) ||
    data.description ||
    "Passionate about creating personalized looks that reflect each client's unique style. Specializing in balayage, vivid colors, and precision cuts."
  const photo = (entityData?.photo_url as string) || data.photo || ''
  const hoverPhoto = data.hoverPhoto || ''
  const preset = getPreset(data.preset)
  const bookingLink = data.bookingLink || '#'

  return (
    <div data-testid="card-stylist-flip" className="group" style={{ perspective: '1200px' }}>
      {/* Gradient glow border wrapper */}
      <div
        className="stylist-card-border-wrapper"
        style={{
          padding: 3,
          borderRadius: '1rem',
          background: preset.frameGradient,
          boxShadow: `0 0 12px ${preset.glowFrom}, 0 0 25px ${preset.glowTo}`,
          transition: 'box-shadow 0.5s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = `0 0 18px ${preset.glowFrom}, 0 0 35px ${preset.glowTo}`
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = `0 0 12px ${preset.glowFrom}, 0 0 25px ${preset.glowTo}`
        }}
      >
        {/* 3D flip inner */}
        <div
          className="flip-card-inner"
          style={{
            borderRadius: 'calc(1rem - 3px)',
            ...(isFlipped ? { transform: 'rotateY(180deg)' } : {}),
          }}
        >
          {/* ═══════════════ FRONT FACE ═══════════════ */}
          <div
            className="flip-card-front"
            style={{
              background: preset.bgGradient,
              borderRadius: 'calc(1rem - 3px)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '28px 20px 20px', textAlign: 'center' }}>
              {/* Action buttons (top-right) */}
              <div
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  display: 'flex',
                  gap: 6,
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsFlipped(true)
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.12)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
                  }}
                >
                  🛍 Products
                </button>
              </div>

              {/* Circular photo with gradient border */}
              <div
                style={{
                  position: 'relative',
                  width: 96,
                  height: 96,
                  margin: '0 auto 16px',
                  borderRadius: '50%',
                  padding: 3,
                  background: preset.frameGradient,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {photo ? (
                    <>
                      <img
                        src={photo}
                        alt={name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'opacity 0.4s ease',
                        }}
                        className="group-hover:opacity-0"
                      />
                      {hoverPhoto && (
                        <img
                          src={hoverPhoto}
                          alt={`${name} alternate`}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                          }}
                          className="group-hover:opacity-100"
                        />
                      )}
                    </>
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        background:
                          'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span
                        style={{ fontSize: 28, fontWeight: 700, color: 'rgba(255,255,255,0.2)' }}
                      >
                        {name.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Name + Title */}
              <h4
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#fafafa',
                  letterSpacing: '-0.01em',
                  marginBottom: 2,
                }}
              >
                {name}
              </h4>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: preset.accentText,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 14,
                  opacity: 0.7,
                }}
              >
                {title}
              </p>

              {/* Collapsible Bio */}
              {bio && (
                <div style={{ marginBottom: 16 }}>
                  <p
                    style={{
                      fontSize: 12,
                      lineHeight: 1.6,
                      color: 'rgba(255,255,255,0.35)',
                      overflow: 'hidden',
                      maxHeight: bioExpanded ? 200 : 40,
                      transition: 'max-height 0.4s ease',
                    }}
                  >
                    {bio}
                  </p>
                  {bio.length > 80 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setBioExpanded(!bioExpanded)
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 11,
                        fontWeight: 600,
                        color: preset.accentText,
                        marginTop: 4,
                        padding: 0,
                        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                      }}
                    >
                      {bioExpanded ? 'Show less ↑' : 'Read more ↓'}
                    </button>
                  )}
                </div>
              )}

              {/* CTA Buttons */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <a
                  href={bookingLink}
                  style={{
                    display: 'inline-block',
                    padding: '8px 20px',
                    borderRadius: 8,
                    background: preset.buttonBg,
                    color: preset.buttonText,
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: 'none',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = `0 4px 16px ${preset.glowFrom}`
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  Book Appointment
                </a>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsFlipped(true)
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease',
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
                  }}
                >
                  See Products
                </button>
              </div>
            </div>
          </div>

          {/* ═══════════════ BACK FACE ═══════════════ */}
          <div
            className="flip-card-back"
            style={{
              background: preset.bgGradient,
              borderRadius: 'calc(1rem - 3px)',
              overflow: 'hidden',
              position: 'absolute',
              inset: 0,
            }}
          >
            <div
              style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 16,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: preset.frameGradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#fff',
                    }}
                  >
                    {name.charAt(0)}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#fafafa' }}>
                    {name}&apos;s Favorites
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsFlipped(false)
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease',
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.12)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
                  }}
                >
                  ↩ Flip Back
                </button>
              </div>

              {/* Product List */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                {DEMO_PRODUCTS.map((product) => (
                  <div
                    key={product.name}
                    style={{
                      display: 'flex',
                      gap: 10,
                      padding: 10,
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.05)',
                    }}
                  >
                    {/* Product color swatch placeholder */}
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        flexShrink: 0,
                        background: preset.frameGradient,
                        opacity: 0.6,
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          color: preset.accentText,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: 1,
                        }}
                      >
                        {product.brand}
                      </div>
                      <div
                        style={{ fontSize: 12, fontWeight: 600, color: '#fafafa', marginBottom: 2 }}
                      >
                        {product.name}
                      </div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
                        {product.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer CTA */}
              <a
                href={bookingLink}
                style={{
                  display: 'block',
                  textAlign: 'center',
                  padding: '10px 16px',
                  marginTop: 12,
                  borderRadius: 8,
                  background: preset.buttonBg,
                  color: preset.buttonText,
                  fontSize: 12,
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'transform 0.2s',
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                }}
              >
                Book with {name}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
